import { useEffect, useRef, useState, useCallback } from 'react';
import { getWsBaseUrl } from '../utils/env';

export interface TranscriptEvent {
  text: string;
  isFinal: boolean;
  id: string;
}

export function useVoiceStream(token: string) {
  const wsRef = useRef<WebSocket | null>(null);
  const [connected, setConnected] = useState(false);
  const [transcripts, setTranscripts] = useState<TranscriptEvent[]>([]);
  const [wpm, setWpm] = useState(0);
  const [recording, setRecording] = useState(false);
  const retryRef = useRef<number>(0);

  const start = useCallback(async () => {
    if (wsRef.current) return;
    const base = getWsBaseUrl() || (typeof window !== 'undefined' ? window.location.origin.replace(/^http/, 'ws') : 'ws://localhost:3000');
    const ws = new WebSocket(`${base}/voice/stream?token=${token}`);
    ws.binaryType = 'arraybuffer';
    ws.onopen = () => setConnected(true);
    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
    };
    ws.onmessage = (ev) => {
      try {
        if (typeof ev.data === 'string') {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'transcript') {
            setTranscripts((t) => [...t, msg]);
            // Simple WPM calc
            const words = msg.text.split(/\s+/).length;
            setWpm(Math.round((words / 0.02))); // per 20ms frame
          }
          if (msg.error === 'rate') {
            // handle rate limit
            setRecording(false);
          }
        }
      } catch {}
    };
    wsRef.current = ws;
  }, [token]);

  const stop = useCallback(() => {
    wsRef.current?.close();
    wsRef.current = null;
    setRecording(false);
  }, []);

  const sendFrame = useCallback((opus: Uint8Array) => {
    wsRef.current?.send(opus);
  }, []);

  useEffect(() => {
    if (retryRef.current > 0) {
      const id = setInterval(() => {
        retryRef.current -= 1;
        if (retryRef.current <= 0) {
          start();
          clearInterval(id);
        }
      }, 1000);
      return () => clearInterval(id);
    }
  }, [start]);

  const attachAudio = useCallback((audioCtx: AudioContext) => {
    // Inject worklet & worker
    (async () => {
      await audioCtx.audioWorklet.addModule('/audio/pcm-worklet.js');
      const mic = await navigator.mediaDevices.getUserMedia({ audio: true });
      const src = audioCtx.createMediaStreamSource(mic);
      const node = new AudioWorkletNode(audioCtx, 'pcm-processor');
      const worker = new Worker('/audio/opus-encoder.worker.js');
      worker.postMessage({ type: 'init' });
      node.port.onmessage = (ev) => {
        worker.postMessage({ type: 'pcm', payload: ev.data }, [ev.data.buffer]);
      };
      worker.onmessage = (ev) => {
        sendFrame(new Uint8Array(ev.data));
      };
      src.connect(node);
    })();
  }, [sendFrame]);

  const handleMsg = useCallback((msg: any) => {
    if (msg.error === 'rate') {
      retryRef.current = msg.retryAfter || 60;
      setRecording(false);
      return;
    }
    if (msg.type === 'tts') {
      const audio = new Audio('data:audio/opus;base64,' + msg.data);
      audio.play();
    }
    if (msg.type === 'stats') setWpm(msg.wpm);
  }, []);

  useEffect(() => {
    const ws = wsRef.current;
    if (!ws) return;
    const listener = (ev: MessageEvent) => {
      if (typeof ev.data === 'string') handleMsg(JSON.parse(ev.data));
    };
    ws.addEventListener('message', listener);
    return () => ws.removeEventListener('message', listener);
  }, [handleMsg]);

  return { connected, transcripts, wpm, start, stop, sendFrame, recording, setRecording, attachAudio };
}
